import { useEffect, useMemo, useState } from "react";
import { api, buildViewerLoginPageUrl } from "../lib/api";
import { Skull, Users, Clock, LogIn, Sparkles, Coins, Heart, Swords, Shield, Search, Wrench, Dumbbell, Package, HardHat, Zap } from "lucide-react";
import { currencyLabel } from "../lib/currencyDisplay";
import PublicSiteLayout from "../components/PublicSiteLayout";

const SKILL_ICONS = {
  attack: Swords,
  defense: Shield,
  strength: Dumbbell,
  magic: Sparkles,
  scavenging: Search,
  tinkering: Wrench,
};
const ITEM_ICONS = { Swords, Sword: Swords, Shield, Sparkles, Package, HardHat, Wrench, Heart, Zap };
const RARITY_FILTERS = ["common", "uncommon", "rare", "epic", "legendary"];
const ITEM_TYPE_FILTERS = ["weapon", "head", "body", "trinket", "consumable"];
const RPG_TABS = ["quests", "character", "inventory", "shop"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
const mediaUrl = (url) => !url ? "" : String(url).startsWith("http") ? url : `${BACKEND_URL}${url}`;
const DUNGEON_TOKEN_KEY = "dungeon_tokens";
const priceText = (price = {}) => {
  const amount = Number(price[DUNGEON_TOKEN_KEY] || 0);
  return amount > 0 ? `${amount} Dungeon Tokens` : "Free";
};
const QUEST_SCALING = [
  {
    label: "Safest",
    commands: "!quest free / !quest bunny100 / !quest rat25",
    detail: "Tier 1. Best for daily play, low HP, new characters, or testing the system.",
  },
  {
    label: "Moderate",
    commands: "!quest bunny500 / !quest rat100",
    detail: "Tier 2. Better rewards and more danger. Heal first if you are already hurt.",
  },
  {
    label: "Dangerous",
    commands: "!quest bunny2000 / !quest rat500",
    detail: "Tier 3. Better rare odds, more steps, and real wipe risk for wounded players.",
  },
  {
    label: "High risk",
    commands: "!quest bunny5000 / !quest rat2000",
    detail: "Tier 4. Big spend, best generated odds, and the most likely place to die.",
  },
];

export default function Dungeons() {
  const [quests, setQuests] = useState([]);
  const [me, setMe] = useState({ authenticated: false });
  const [character, setCharacter] = useState(null);
  const [tick, setTick] = useState(0);
  const [typeFilter, setTypeFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("quests");

  const load = async () => {
    const [{ data: d }, { data: m }] = await Promise.all([
      api.get("/rpg/quests/available"),
      api.get("/viewer/me"),
    ]);
    setQuests(d);
    setMe(m);
    if (m.authenticated) {
      try {
        const { data: c } = await api.get("/rpg/character/me");
        setCharacter(c);
      } catch {
        setCharacter(null);
      }
    } else {
      setCharacter(null);
    }
  };

  useEffect(() => { load(); const id = setInterval(load, 3000); return () => clearInterval(id); }, []);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(id); }, []);

  const specialQuests = quests.filter(q => q.event_quest || ["event", "seasonal"].includes(q.quest_type));
  const typeOptions = useMemo(() => ["all", ...Array.from(new Set(specialQuests.map(q => q.quest_type).filter(Boolean)))], [specialQuests]);
  const visibleQuests = specialQuests.filter(q => (
    (typeFilter === "all" || q.quest_type === typeFilter)
  ));

  return (
    <PublicSiteLayout>
      <div className="rpg-page relative max-w-7xl mx-auto">
        <section className="public-page-hero">
          <div className="label-eyebrow mb-3 text-[hsl(var(--currency-event))]">Barracks RPG</div>
          <h1 className="flex items-center gap-4">
            <Skull className="w-12 h-12 sm:w-16 sm:h-16 text-[hsl(var(--currency-event))]" /> Quests
          </h1>
          <p className="text-lg text-[hsl(var(--muted-foreground))] mt-5 max-w-4xl leading-8">
            Type <code className="font-mono text-white">!quest</code> in chat for options, <code className="font-mono text-white">!quest free</code> for your daily run,
            or spend Bunny Tokens/Rat Coins with <code className="font-mono text-white">!quest bunny500</code> and <code className="font-mono text-white">!quest rat100</code>.
            Runs start automatically when the join timer ends.
          </p>
          <div className="mt-7">
            {me.authenticated ? (
              <span className="inline-flex text-sm text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))] bg-black/50 px-4 py-3 rounded">Signed in as @{me.viewer.login}</span>
            ) : (
              <a href={buildViewerLoginPageUrl("/rpg")} target="_top" rel="noopener" className="inline-flex text-sm uppercase tracking-widest px-5 py-3 rounded bg-[hsl(var(--currency-event))] text-white items-center gap-2">
                <LogIn className="w-4 h-4" /> Sign in
              </a>
            )}
          </div>
        </section>

        <div className="rpg-explainer mt-8 pt-6 border-t border-white/10">
          <div className="text-lg font-semibold">How quests and dungeons work</div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 text-sm leading-6 text-[hsl(var(--muted-foreground))]">
            <div className="surface-2 p-3"><span className="text-white">Simple quests</span> are generated from the command you type. Higher Bunny Token or Rat Coin entries mean a harder run with better odds, but rare drops stay rare.</div>
            <div className="surface-2 p-3"><span className="text-white">Daily free</span> means each signed-in player gets one free generated run per day.</div>
            <div className="surface-2 p-3"><span className="text-white">Special quests</span> are hand-built seasonal or event runs. Use <code className="text-white">!quest list</code> to see active ones.</div>
            <div className="surface-2 p-3"><span className="text-white">Quest loot</span> stays in your inventory after the run, even if a seasonal quest later disappears.</div>
            <div className="surface-2 p-3"><span className="text-white">Cooldowns</span> slow down new quest starts after a run begins or ends. You can still type <code className="text-white">!join</code> while a party is forming.</div>
          </div>
        </div>

        {me.authenticated && (
          <div className="mt-8 surface-2 p-2 flex flex-wrap gap-2 w-fit">
            {RPG_TABS.map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`text-sm uppercase tracking-widest px-5 py-3 rounded ${activeTab === tab ? "bg-[hsl(var(--currency-event))] text-white" : "text-[hsl(var(--muted-foreground))] hover:text-white"}`}
              >
                {tab === "quests" ? "quest board" : tab}
              </button>
            ))}
          </div>
        )}

        {me.authenticated && activeTab === "character" && (
          <CharacterSheetSection character={character} onReload={load} view="character" />
        )}

        {me.authenticated && activeTab === "inventory" && (
          <CharacterSheetSection character={character} onReload={load} view="inventory" />
        )}

        {me.authenticated && activeTab === "shop" && (
          <RpgShopSection />
        )}

        {(!me.authenticated || activeTab === "quests") && (
          <>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="surface p-6">
            <div className="label-eyebrow text-[hsl(var(--currency-event))]">Daily</div>
            <div className="text-2xl font-semibold mt-2">Free generated quest</div>
            <p className="text-sm leading-6 text-[hsl(var(--muted-foreground))] mt-3">
              Each viewer gets one free generated quest per day. The run still opens a join timer so chat can party up.
            </p>
            <code className="block mt-5 text-base font-mono text-white">!quest free</code>
          </div>
          <div className="surface p-6">
            <div className="label-eyebrow text-[hsl(var(--currency-event))]">Bunny Tokens</div>
            <div className="text-2xl font-semibold mt-2">Spend free currency</div>
            <p className="text-sm leading-6 text-[hsl(var(--muted-foreground))] mt-3">
              Put in the amount you want. Larger runs are harder and improve rewards without turning rare loot into a giveaway.
            </p>
            <code className="block mt-5 text-base font-mono text-white">!quest bunny500</code>
          </div>
          <div className="surface p-6">
            <div className="label-eyebrow text-[hsl(var(--currency-event))]">Rat Coins</div>
            <div className="text-2xl font-semibold mt-2">Spend paid currency</div>
            <p className="text-sm leading-6 text-[hsl(var(--muted-foreground))] mt-3">
              Rat Coin quests use smaller paid-currency amounts than Bunny Token quests for the same rough risk tier.
            </p>
            <code className="block mt-5 text-base font-mono text-white">!quest rat100</code>
          </div>
        </div>

        <div className="mt-6 surface-2 p-5">
          <div className="text-lg font-semibold">Quest command</div>
          <div className="mt-3 text-sm leading-6 text-[hsl(var(--muted-foreground))]">
            Type <code className="text-white">!quest</code> to see your available range and whether your free quest is ready. Type <code className="text-white">!join</code> while a run is assembling to join someone else's party.
          </div>
        </div>

        <div className="mt-6 surface p-5">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex-1">
              <div className="label-eyebrow text-[hsl(var(--currency-event))]">Difficulty scaling</div>
              <div className="text-2xl font-semibold mt-1">Spend more only when you are ready</div>
              <p className="text-sm leading-6 text-[hsl(var(--muted-foreground))] mt-3 max-w-4xl">
                Generated quests scale from the currency amount you put in. Higher tiers improve rewards and rare loot odds, but they also add harder checks and more damage. If your HP is low, buy or use healing before starting a bigger run.
              </p>
            </div>
            {character?.viewer && (
              <div className="surface-2 px-4 py-3 text-sm">
                <div className="label-eyebrow">Your status</div>
                <div className="mt-1 font-mono text-white">Lv {character.viewer.level || 1} - HP {character.viewer.hp}/{character.viewer.hp_max}</div>
              </div>
            )}
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {QUEST_SCALING.map(row => (
              <div key={row.label} className="surface-2 p-4">
                <div className="text-sm uppercase tracking-widest text-[hsl(var(--currency-event))]">{row.label}</div>
                <code className="block mt-3 text-sm text-white leading-6">{row.commands}</code>
                <p className="mt-3 text-sm leading-6 text-[hsl(var(--muted-foreground))]">{row.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {specialQuests.length > 0 && (
          <div className="mt-8">
            <div className="surface-2 p-4 flex flex-wrap items-center gap-4">
              <div>
                <div className="label-eyebrow mb-1">Special quest type</div>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded px-4 py-3 text-base min-w-48">
                  {typeOptions.map(option => <option key={option} value={option}>{option === "all" ? "All special types" : option}</option>)}
                </select>
              </div>
              <div className="text-sm text-[hsl(var(--muted-foreground))] ml-auto">
                Showing {visibleQuests.length} active special quests
              </div>
            </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
          {visibleQuests.length === 0 && (
            <div className="surface p-6 col-span-full text-sm text-[hsl(var(--muted-foreground))]">No special quests matched those filters.</div>
          )}
          {visibleQuests.map(d => {
            const cost = d.daily_free
              ? (d.daily_consumed ? `${Number(d.entry_cost || 0)} ${currencyLabel({ id: d.currency_id })}` : "Daily free")
              : (d.entry_cost > 0 ? `${d.entry_cost} ${currencyLabel({ id: d.currency_id })}` : "Free");
            const wallet = d.reward_config?.wallet || {};
            const materials = d.reward_config?.materials || {};
            const active = d.active_run;
            const repeatApplies = d.daily_free && d.daily_consumed;
            const joinSecondsLeft = active?.join_window_until
              ? Math.max(0, Math.floor((new Date(active.join_window_until).getTime() - Date.now()) / 1000))
              : 0;
            const isAssembling = active?.status === "assembling";
            return (
              <div key={d.id} className="surface p-6 flex flex-col gap-4" data-testid={`dungeon-${d.slug}`}>
                <div className="min-h-32 rounded bg-black/35 border border-[hsl(var(--border))] p-5 flex flex-col justify-between">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm uppercase tracking-widest text-[hsl(var(--primary))]">{d.quest_type || "quest"}</span>
                    <span className="text-sm font-mono text-[hsl(var(--muted-foreground))]">Lv {d.recommended_level || 1}+</span>
                  </div>
                  <div className="mt-5 text-sm text-[hsl(var(--muted-foreground))] line-clamp-2">{d.artwork_reference || d.quest_giver_description || "A quest notice waits on the barracks board."}</div>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="label-eyebrow">{d.quest_type || "quest"} {d.daily_free ? " - Free once/day" : d.event_quest ? " - Event quest" : ""}</div>
                    <div className="text-2xl font-semibold mt-1">{d.title || d.name}</div>
                    <code className="text-sm font-mono text-[hsl(var(--muted-foreground))]">Special: !quest {d.slug}</code>
                  </div>
                  {repeatApplies && <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Normal cost</span>}
                  {d.stream_limit_consumed && <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Started this stream</span>}
                </div>
                <p className="text-base leading-7 text-[hsl(var(--muted-foreground))]">{d.description}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="font-mono">{cost}</span>
                  <span className="flex items-center gap-1 text-[hsl(var(--muted-foreground))]"><Users className="w-4 h-4" /> Open party</span>
                  <span className="flex items-center gap-1 text-[hsl(var(--muted-foreground))]">Lv {d.recommended_level || 1}+</span>
                  <span className="flex items-center gap-1 text-[hsl(var(--muted-foreground))]"><Clock className="w-4 h-4" /> {d.duration_seconds || d.encounter_count * 5}s</span>
                </div>
                <div className="flex flex-wrap gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                  {Object.entries(wallet).slice(0, 3).map(([k, v]) => Number(v) > 0 && <span key={k} className="inline-flex items-center gap-1"><Coins className="w-4 h-4" /> {priceText({ [k]: v })}</span>)}
                  {Object.entries(materials).slice(0, 3).map(([k, v]) => Number(v) > 0 && <span key={k} className="inline-flex items-center gap-1"><Sparkles className="w-4 h-4" /> {v} {k.replaceAll("_", " ")}</span>)}
                </div>
                {active && (
                  <div className="mt-1 surface-2 p-4 text-sm" data-testid={`dungeon-active-${d.slug}`}>
                    <div className="flex items-center gap-2">
                      <span className={`status-dot ${active.status === "running" ? "live" : "online"}`} />
                      <span className="label-eyebrow">{active.status}</span>
                      <span className="ml-auto">Party: {active.party_size}</span>
                    </div>
                    {isAssembling && (
                      <div className="mt-2 text-[hsl(var(--muted-foreground))]">
                        Type <code className="font-mono text-white">!join</code> in chat - {joinSecondsLeft}s left
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
          </div>
        )}
          </>
        )}

        <div className="mt-12 text-sm text-[hsl(var(--muted-foreground))] text-center">
          Character sheet, gear, loot, Dungeon Tokens, and materials now live here on the RPG page.
        </div>
      </div>
    </PublicSiteLayout>
  );
}

function RpgShopSection() {
  const [stock, setStock] = useState([]);
  const [character, setCharacter] = useState(null);
  const [busy, setBusy] = useState("");
  const loadShop = async () => {
    const [{ data: shop }, { data: c }] = await Promise.all([
      api.get("/rpg/shop"),
      api.get("/rpg/character/me"),
    ]);
    setStock(shop);
    setCharacter(c);
  };
  useEffect(() => { loadShop(); }, []);
  const buy = async (itemId) => {
    if (busy) return;
    setBusy(itemId);
    try {
      await api.post("/rpg/shop/buy", { item_id: itemId });
      await loadShop();
    } finally {
      setBusy("");
    }
  };
  const wallet = character?.wallet || {};
  const canAfford = (price) => Number(wallet[DUNGEON_TOKEN_KEY] || 0) >= Number((price || {})[DUNGEON_TOKEN_KEY] || 0);
  const materials = Object.entries(character?.materials || {}).filter(([, amount]) => Number(amount) > 0);
  const sellMaterial = async (material, quantity = 1) => {
    setBusy(`material:${material}`);
    try {
      await api.post("/rpg/materials/sell", { material, quantity });
      await loadShop();
    } finally {
      setBusy("");
    }
  };
  return (
    <section className="mt-6 space-y-5">
      <div className="surface p-6 bg-black/75">
        <div className="label-eyebrow text-[hsl(var(--currency-event))]">RPG shop</div>
        <h2 className="text-3xl font-black uppercase tracking-normal text-[hsl(var(--foreground))] mt-1">Dungeon Token Market</h2>
        <p className="mt-2 text-sm leading-6 text-[hsl(var(--muted-foreground))]">
          Spend Dungeon Tokens from quests, events, sold gear, and sold materials.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.35fr] gap-5">
        <div className="space-y-5">
          <div className="surface p-5 bg-black/70">
            <div className="label-eyebrow mb-3">Dungeon Token wallet</div>
            <div className="border border-[hsl(var(--border))] rounded p-4">
              <div className="label-eyebrow">Dungeon Tokens</div>
              <div className="mt-1 font-mono text-2xl text-[hsl(var(--foreground))]">{Number(wallet[DUNGEON_TOKEN_KEY] || 0)}</div>
              <div className="mt-2 text-xs leading-5 text-[hsl(var(--muted-foreground))]">
                One RPG currency for shop purchases, gear sales, material sales, quest rewards, and event rewards.
              </div>
            </div>
          </div>

          <div className="surface p-5 bg-black/70">
            <div className="label-eyebrow mb-3">Materials</div>
            {materials.length === 0 ? (
              <div className="text-sm text-[hsl(var(--muted-foreground))]">No gathered materials to sell.</div>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {materials.map(([material, amount]) => (
                  <div key={material} className="rounded border border-[hsl(var(--border))] px-3 py-2 bg-black/30 flex items-center gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-sm text-[hsl(var(--foreground))]">{amount} {material.replaceAll("_", " ")}</div>
                    </div>
                    <div className="ml-auto flex gap-2">
                      <button
                        type="button"
                        disabled={busy === `material:${material}`}
                        onClick={() => sellMaterial(material, 1)}
                        className="text-[10px] uppercase tracking-widest border border-[hsl(var(--border))] rounded px-3 py-2 text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50"
                      >
                        Sell 1
                      </button>
                      <button
                        type="button"
                        disabled={busy === `material:${material}`}
                        onClick={() => sellMaterial(material, Number(amount))}
                        className="text-[10px] uppercase tracking-widest border border-[hsl(var(--currency-event))] rounded px-3 py-2 text-white bg-[hsl(var(--currency-event))]/20 hover:bg-[hsl(var(--currency-event))]/35 disabled:opacity-50"
                      >
                        Sell all
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="surface p-5 bg-black/70">
          <div className="label-eyebrow mb-3">Shop stock</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {stock.map((entry, index) => {
          const affordable = canAfford(entry.price);
          const type = entry.shop_type || "item";
          const statText = Object.entries(entry.item?.stats || {}).map(([k, v]) => `${k} +${v}`).join(", ");
          const materialText = Object.entries(entry.materials || {}).filter(([, v]) => Number(v) > 0).map(([k, v]) => `${v} ${k.replaceAll("_", " ")}`).join(", ");
          const rarityOdds = Object.entries(entry.loot_generation?.rarity_weights || {}).filter(([, v]) => Number(v) > 0).map(([k, v]) => `${k} ${v}`).join(", ");
          const imageUrl = mediaUrl(entry.item?.image_url || entry.image_url);
          return (
            <div key={`${entry.id || entry.name || "shop"}-${index}`} className="surface-2 p-4 flex flex-col gap-3">
              <ItemArt
                imageUrl={imageUrl}
                icon={type === "loot_crate" ? "Package" : type === "material_pack" ? "Sparkles" : entry.item?.icon || "Heart"}
                rarityColor={entry.item?.rarity_color || "hsl(var(--currency-event))"}
                className="h-32 w-full"
              />
              <div className="flex items-center gap-2">
                {type === "loot_crate" ? <Package className="w-5 h-5 text-[hsl(var(--currency-event))]" /> : type === "material_pack" ? <Sparkles className="w-5 h-5 text-[hsl(var(--currency-event))]" /> : <Heart className="w-5 h-5 text-[hsl(var(--destructive))]" />}
                <div className="font-semibold">{entry.name}</div>
              </div>
              <div className="text-sm leading-6 text-[hsl(var(--muted-foreground))]">{entry.description}</div>
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                {type === "loot_crate"
                  ? `Loot crate${rarityOdds ? ` - ${rarityOdds}` : ""}`
                  : type === "material_pack"
                    ? `Materials${materialText ? ` - ${materialText}` : ""}`
                    : statText || entry.item?.item_type || "Item"}
              </div>
              <div className="font-mono text-sm text-[hsl(var(--foreground))]">{priceText(entry.price)}</div>
              <button
                type="button"
                disabled={!affordable || Boolean(busy)}
                onClick={() => buy(entry.id)}
                className="mt-auto text-xs uppercase tracking-widest rounded px-3 py-2 border border-[hsl(var(--currency-event))] text-white bg-[hsl(var(--currency-event))]/20 hover:bg-[hsl(var(--currency-event))]/35 disabled:opacity-50"
              >
                {affordable ? "Buy" : "Need more tokens"}
              </button>
            </div>
          );
        })}
          </div>
        </div>
      </div>
    </section>
  );
}

function ItemArt({ imageUrl, icon, rarityColor, className = "w-9 h-9" }) {
  const [failed, setFailed] = useState(false);
  const Icon = ITEM_ICONS[icon] || Package;
  useEffect(() => setFailed(false), [imageUrl]);
  if (imageUrl && !failed) {
    return <img src={imageUrl} alt="" onError={() => setFailed(true)} className={`${className} rounded object-cover border border-[hsl(var(--border))] shrink-0`} />;
  }
  return (
    <div
      className={`${className} rounded border shrink-0 flex items-center justify-center overflow-hidden`}
      style={{
        borderColor: rarityColor,
        background: `linear-gradient(135deg, ${rarityColor}33, rgba(0,0,0,0.78))`,
        boxShadow: `inset 0 0 28px ${rarityColor}22`,
      }}
    >
      <Icon className="w-1/2 h-1/2" style={{ color: rarityColor }} />
    </div>
  );
}

function toggleFilterValue(values, value) {
  return values.includes(value) ? values.filter(v => v !== value) : [...values, value];
}

function FilterCheckboxes({ label, options, selected, onChange }) {
  return (
    <div>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map(option => (
          <label key={option} className="text-xs uppercase tracking-widest border border-[hsl(var(--border))] rounded px-3 py-2 flex items-center gap-2 bg-black/25">
            <input type="checkbox" checked={selected.includes(option)} onChange={() => onChange(toggleFilterValue(selected, option))} />
            {option}
          </label>
        ))}
      </div>
    </div>
  );
}

function CharacterSheetSection({ character, onReload, view = "character" }) {
  const [busy, setBusy] = useState("");
  const [rarityFilters, setRarityFilters] = useState([]);
  const [typeFilters, setTypeFilters] = useState([]);
  if (!character) {
    return (
      <div className="mt-6 surface p-6 text-sm text-[hsl(var(--muted-foreground))] bg-black/70">
        Loading your character sheet...
      </div>
    );
  }

  const equip = async (itemId) => {
    setBusy(itemId);
    try {
      await api.post("/rpg/equipment/equip", { item_id: itemId });
      await onReload();
    } finally {
      setBusy("");
    }
  };

  const unequip = async (slot) => {
    setBusy(`slot:${slot}`);
    try {
      await api.post("/rpg/equipment/unequip", { slot });
      await onReload();
    } finally {
      setBusy("");
    }
  };
  const sell = async (itemId) => {
    setBusy(`sell:${itemId}`);
    try {
      await api.post("/rpg/inventory/sell", { item_id: itemId });
      await onReload();
    } finally {
      setBusy("");
    }
  };
  const consumeItem = async (itemId) => {
    setBusy(`use:${itemId}`);
    try {
      await api.post("/rpg/inventory/use", { item_id: itemId });
      await onReload();
    } finally {
      setBusy("");
    }
  };
  const inventory = character.inventory || [];
  const filteredInventory = inventory.filter(item => {
    const type = item.item_type || item.slot || (item.consumable ? "consumable" : "item");
    return (rarityFilters.length === 0 || rarityFilters.includes(item.rarity)) &&
      (typeFilters.length === 0 || typeFilters.includes(type));
  });
  const showingCharacter = view === "character";
  const showingInventory = view === "inventory";

  return (
    <section className="mt-8 space-y-5">
      <div className="surface p-6 bg-black/75">
        <div className="label-eyebrow text-[hsl(var(--currency-event))]">Character sheet</div>
        <h2 className="text-3xl font-black uppercase tracking-normal text-[hsl(var(--foreground))] mt-1">{showingInventory ? "Inventory" : "Gear and skills"}</h2>
        <p className="mt-2 text-sm leading-6 text-[hsl(var(--muted-foreground))]">
          {showingInventory ? "Filter loot by rarity and type, equip gear, use consumables, or sell generated items." : "Track HP, level, and skill progress."}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5">
        {showingCharacter && (
        <div className="space-y-5">
          <div className="surface p-5 bg-black/70">
            <div className="flex items-center justify-between mb-4">
              <div className="label-eyebrow flex items-center gap-2"><Heart className="w-4 h-4 text-[hsl(var(--destructive))]" /> HP</div>
              <span className="font-mono text-sm">{character.viewer.hp} / {character.viewer.hp_max}</span>
            </div>
            <div className="mb-3 text-sm text-[hsl(var(--muted-foreground))]">
              Character level <span className="font-mono text-[hsl(var(--foreground))]">{character.viewer.level || 1}</span> is based on your average skill level.
            </div>
            <div className="h-2 bg-[hsl(var(--surface-2))] rounded overflow-hidden">
              <div className="h-full bg-[hsl(var(--destructive))] transition-all" style={{ width: `${100 * (character.viewer.hp || 0) / Math.max(1, character.viewer.hp_max || 1)}%` }} />
            </div>

            <div className="mt-6 label-eyebrow mb-3">Skills</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {character.skills.map(s => {
                const Icon = SKILL_ICONS[s.id] || Sparkles;
                const pct = 100 * s.xp_into_level / Math.max(1, s.xp_to_next);
                return (
                  <div key={s.id} className="border border-[hsl(var(--border))] rounded p-3" data-testid={`rpg-skill-${s.id}`}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: s.color }} />
                      <span className="text-sm font-medium">{s.name}</span>
                      <span className="ml-auto font-mono text-sm" style={{ color: s.color }}>Lv {s.level}</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-[hsl(var(--surface-2))] rounded overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: s.color }} />
                    </div>
                    <div className="mt-1 font-mono text-[10px] text-[hsl(var(--muted-foreground))]">{s.xp_into_level} / {s.xp_to_next} XP</div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
        )}

        {showingInventory && (
          <div className="surface p-5 bg-black/70">
            <div className="label-eyebrow mb-3">Currently equipped</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
              {["head", "body", "weapon", "trinket"].map(slot => {
                const eq = character.equipment?.[slot];
                const item = eq ? character.inventory.find(i => i.item_id === eq) : null;
                const Icon = item ? (ITEM_ICONS[item.icon] || Package) : Package;
                return (
                  <div key={slot} className="border border-[hsl(var(--border))] rounded p-3" data-testid={`rpg-slot-${slot}`} style={item ? { borderColor: item.rarity_color } : undefined}>
                    <div className="label-eyebrow">{slot}</div>
                    {item ? (
                      <>
                        <div className="mt-2 flex items-start gap-2">
                          <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: item.rarity_color }} />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-[hsl(var(--foreground))]">{item.name}</div>
                            <div className="mt-1 text-[10px] uppercase tracking-widest" style={{ color: item.rarity_color }}>{item.rarity}</div>
                          </div>
                        </div>
                        {item.stats && Object.keys(item.stats).length > 0 && (
                          <div className="mt-2 text-[11px] font-mono text-[hsl(var(--muted-foreground))]">
                            {Object.entries(item.stats).map(([k, v]) => `${k} +${v}`).join(", ")}
                          </div>
                        )}
                        <button
                          type="button"
                          disabled={busy === `slot:${slot}`}
                          onClick={() => unequip(slot)}
                          className="mt-3 text-xs uppercase tracking-widest border border-[hsl(var(--border))] rounded px-3 py-2 text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50"
                        >
                          Unequip
                        </button>
                      </>
                    ) : (
                      <div className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Empty</div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow flex items-center gap-2"><Package className="w-4 h-4" /> Inventory</div>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">
                {character.inventory_slots_used ?? inventory.length} / {character.max_inventory_slots ?? 250} slots
                {filteredInventory.length !== inventory.length ? ` (${filteredInventory.length} shown)` : ""}
              </span>
            </div>
            <div className="mb-5 grid grid-cols-1 lg:grid-cols-2 gap-4 surface-2 p-4">
              <FilterCheckboxes label="Rarity" options={RARITY_FILTERS} selected={rarityFilters} onChange={setRarityFilters} />
              <FilterCheckboxes label="Type" options={ITEM_TYPE_FILTERS} selected={typeFilters} onChange={setTypeFilters} />
            </div>
            {inventory.length === 0 ? (
              <div className="text-sm text-[hsl(var(--muted-foreground))]">Nothing yet - run a quest to score loot.</div>
            ) : filteredInventory.length === 0 ? (
              <div className="text-sm text-[hsl(var(--muted-foreground))]">No items match those filters.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {filteredInventory.map(it => {
                  const equipped = it.slot && character.equipment?.[it.slot] === it.item_id;
                  const imageUrl = mediaUrl(it.image_url);
                  const sellValue = priceText(it.sell_value || {});
                  return (
                    <div
                      key={it.item_id}
                      className="surface-2 p-4 flex flex-col gap-2"
                      data-testid={`rpg-inv-${it.item_id}`}
                      style={{ borderLeft: `3px solid ${it.rarity_color}` }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <ItemArt imageUrl={imageUrl} icon={it.icon} rarityColor={it.rarity_color} className="w-12 h-12" />
                          <div className="font-medium text-sm truncate">{it.name}</div>
                        </div>
                        {it.qty > 1 && <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">x{it.qty}</span>}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: it.rarity_color }}>{it.rarity}{it.item_type ? ` - ${it.item_type}` : it.slot ? ` - ${it.slot}` : ""}</div>
                      {it.flavor && <div className="text-xs text-[hsl(var(--muted-foreground))] italic line-clamp-2">{it.flavor}</div>}
                      {it.stats && Object.keys(it.stats).length > 0 && (
                        <div className="text-[11px] font-mono text-[hsl(var(--muted-foreground))]">
                          {Object.entries(it.stats).map(([k, v]) => `${k} +${v}`).join(", ")}
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {it.slot ? (
                          <>
                          <button
                            type="button"
                            disabled={equipped || busy === it.item_id}
                            onClick={() => equip(it.item_id)}
                            className="text-xs uppercase tracking-widest rounded px-3 py-2 border border-[hsl(var(--currency-event))] text-white bg-[hsl(var(--currency-event))]/20 hover:bg-[hsl(var(--currency-event))]/35 disabled:opacity-50"
                            data-testid={`rpg-equip-${it.item_id}`}
                          >
                            {equipped ? "Equipped" : "Equip"}
                          </button>
                          </>
                        ) : it.consumable ? (
                          <button
                            type="button"
                            disabled={busy === `use:${it.item_id}` || Number(character.viewer.hp || 0) >= Number(character.viewer.hp_max || 0)}
                            onClick={() => consumeItem(it.item_id)}
                            className="text-xs uppercase tracking-widest rounded px-3 py-2 border border-[hsl(var(--destructive))] text-white bg-[hsl(var(--destructive))]/20 hover:bg-[hsl(var(--destructive))]/35 disabled:opacity-50"
                            data-testid={`rpg-use-${it.item_id}`}
                          >
                            {Number(character.viewer.hp || 0) >= Number(character.viewer.hp_max || 0) ? "HP full" : "Use"}
                          </button>
                        ) : (
                          <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Not equippable</div>
                        )}
                        {(it.generated || it.shop_item || sellValue) && (
                          <button
                            type="button"
                            disabled={busy === `sell:${it.item_id}`}
                            onClick={() => sell(it.item_id)}
                            className="text-xs uppercase tracking-widest rounded px-3 py-2 border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50"
                          >
                            Sell{sellValue ? ` (${sellValue})` : ""}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
